import React from 'react'
import type {
  LotStoryArtifactDetailV1,
  LotStoryArtifactVersionV1
} from '../../types/lotStory'
import type {
  LotStoryLibraryVersionHistoryRowCompareActions,
  LotStoryLibraryVersionHistoryRowCompareState,
  LotStoryLibraryVersionHistoryRowPreviewActions,
  LotStoryLibraryVersionHistoryRowPreviewState,
  TranslateFn
} from './lotStoryLibraryTypes'
import {
  getLotStoryArtifactVersionBadgeStyle,
  getLotStoryArtifactVersionLabel
} from './lotStoryLibraryUi'

type LotStoryLibraryVersionHistoryRowHeaderProps = {
  t: TranslateFn
  artifact: LotStoryArtifactDetailV1
  version: LotStoryArtifactVersionV1
  previewState: LotStoryLibraryVersionHistoryRowPreviewState
  previewActions: LotStoryLibraryVersionHistoryRowPreviewActions
  compareState: LotStoryLibraryVersionHistoryRowCompareState
  compareActions: LotStoryLibraryVersionHistoryRowCompareActions
  formatHistoryTimestamp: (value: string) => string
}

type VersionHistoryRowHeaderState = {
  isCurrentBundle: boolean
  isCurrentDraft: boolean
  isCurrentPublished: boolean
  isSnapshotOpen: boolean
  isCompareBaseVersion: boolean
  isCompareTargetVersion: boolean
}

type VersionHistoryRowStatusBadgesProps = {
  t: TranslateFn
  versionStatus: LotStoryArtifactVersionV1['versionStatus']
  state: VersionHistoryRowHeaderState
}

type VersionHistoryBadge = {
  key: string
  style: React.CSSProperties
  label: string
}

function VersionHistoryRowStatusBadges({
  t,
  versionStatus,
  state
}: Readonly<VersionHistoryRowStatusBadgesProps>) {
  const badges: VersionHistoryBadge[] = [
    {
      key: 'version-status',
      style: {
        ...getLotStoryArtifactVersionBadgeStyle(versionStatus),
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase' as const,
        letterSpacing: 0.3
      },
      label: getLotStoryArtifactVersionLabel(t, versionStatus)
    }
  ]

  if (state.isCurrentBundle) {
    badges.push({
      key: 'current-bundle',
      style: { backgroundColor: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700 },
      label: t('lots.story.library.history.current')
    })
  }

  if (state.isCurrentDraft) {
    badges.push({
      key: 'current-draft',
      style: { backgroundColor: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700 },
      label: t('lots.story.library.history.currentDraft')
    })
  }

  if (state.isCurrentPublished) {
    badges.push({
      key: 'current-published',
      style: { backgroundColor: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700 },
      label: t('lots.story.library.history.currentPublished')
    })
  }

  return (
    <>
      {badges.map((badge) => (
        <span key={badge.key} style={badge.style}>
          {badge.label}
        </span>
      ))}
    </>
  )
}

type VersionHistoryActionButtonProps = {
  isActive: boolean
  activeLabel: string
  inactiveLabel: string
  onClick: () => void
}

function VersionHistoryActionButton({
  isActive,
  activeLabel,
  inactiveLabel,
  onClick
}: Readonly<VersionHistoryActionButtonProps>) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '6px 10px',
        borderRadius: 6,
        border: isActive ? '1px solid #60a5fa' : '1px solid #cbd5e1',
        backgroundColor: isActive ? '#eff6ff' : '#ffffff',
        color: isActive ? '#1d4ed8' : '#0f172a',
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: 600
      }}
    >
      {isActive ? activeLabel : inactiveLabel}
    </button>
  )
}

type VersionHistoryRowActionsProps = {
  t: TranslateFn
  artifact: LotStoryArtifactDetailV1
  version: LotStoryArtifactVersionV1
  state: VersionHistoryRowHeaderState
  formatHistoryTimestamp: (value: string) => string
  previewActions: LotStoryLibraryVersionHistoryRowPreviewActions
  compareActions: LotStoryLibraryVersionHistoryRowCompareActions
}

function VersionHistoryRowActions({
  t,
  artifact,
  version,
  state,
  formatHistoryTimestamp,
  previewActions,
  compareActions
}: Readonly<VersionHistoryRowActionsProps>) {
  const { onOpenArtifactVersion } = previewActions
  const {
    onSelectCompareBaseVersion,
    onToggleCompareTargetVersion
  } = compareActions

  return (
    <div style={{ display: 'grid', gap: 8, justifyItems: 'end' }}>
      <div style={{ fontSize: 12, color: '#64748b' }}>
        {formatHistoryTimestamp(version.createdAt)}
      </div>
      <VersionHistoryActionButton
        isActive={state.isSnapshotOpen}
        activeLabel={t('lots.story.library.history.snapshotOpen')}
        inactiveLabel={t('lots.story.library.history.openSnapshot')}
        onClick={() => onOpenArtifactVersion(artifact, version)}
      />
      <VersionHistoryActionButton
        isActive={state.isCompareBaseVersion}
        activeLabel={t('lots.story.library.compare.baseSelected')}
        inactiveLabel={t('lots.story.library.compare.setBase')}
        onClick={() => onSelectCompareBaseVersion(version.artifactVersionId)}
      />
      {state.isCompareBaseVersion ? null : (
        <VersionHistoryActionButton
          isActive={state.isCompareTargetVersion}
          activeLabel={t('lots.story.library.compare.comparing')}
          inactiveLabel={t('lots.story.library.compare.withCurrent')}
          onClick={() => onToggleCompareTargetVersion(version.artifactVersionId)}
        />
      )}
    </div>
  )
}

export default function LotStoryLibraryVersionHistoryRowHeader({
  t,
  artifact,
  version,
  previewState,
  previewActions,
  compareState,
  compareActions,
  formatHistoryTimestamp
}: Readonly<LotStoryLibraryVersionHistoryRowHeaderProps>) {
  const {
    selectedStoryPreviewVersionId
  } = previewState
  const {
    compareBaseStoryVersionId,
    compareTargetStoryVersionId
  } = compareState
  const rowState: VersionHistoryRowHeaderState = {
    isCurrentBundle: version.artifactVersionId === artifact.version.artifactVersionId,
    isCurrentDraft: version.artifactVersionId === artifact.artifact.currentDraftVersionId,
    isCurrentPublished: version.artifactVersionId === artifact.artifact.currentPublishedVersionId,
    isSnapshotOpen: selectedStoryPreviewVersionId === version.artifactVersionId,
    isCompareBaseVersion: compareBaseStoryVersionId === version.artifactVersionId,
    isCompareTargetVersion: compareTargetStoryVersionId === version.artifactVersionId
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div style={{ display: 'grid', gap: 6 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <strong style={{ fontSize: 14, color: '#111827' }}>{t('lots.story.library.item.version', { version: version.versionNumber })}</strong>
          <VersionHistoryRowStatusBadges t={t} versionStatus={version.versionStatus} state={rowState} />
        </div>
        <div style={{ fontSize: 13, color: '#111827' }}>{version.title}</div>
      </div>

      <VersionHistoryRowActions
        t={t}
        artifact={artifact}
        version={version}
        state={rowState}
        formatHistoryTimestamp={formatHistoryTimestamp}
        previewActions={previewActions}
        compareActions={compareActions}
      />
    </div>
  )
}